import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import { CMSContent } from '../types';
import { compressDataUrl } from './imageCompressor';
import { perfTracker } from './perfLogger';

export const DEFAULT_CMS_CONTENT: CMSContent = {
  heroBadge: 'WORLD-CLASS DIGITAL TEMPLATE MARKETPLACE',
  heroTitle: 'Premium Editable PSD, Vector & Document Templates',
  heroSubtitle: 'Download professionally designed editable PSD, AI, Vector and CDR templates including Passports, ID Cards, Driving Licenses, Certificates, Utility Bills, Bank Statements, Resume Templates, Health ID and many more. Instant download, commercial quality, fully customizable.',
  heroPrimaryCtaText: 'Explore Marketplace',
  heroPrimaryCtaLink: '#products',
  heroSecondaryCtaText: 'Browse Categories',
  heroSecondaryCtaLink: '#categories',
  heroTertiaryCtaText: 'View Premium Templates',
  heroTertiaryCtaLink: '#products',
  heroBgImageUrl: '',
  heroBgVideoUrl: '',
  heroOverlayOpacity: 0.8,
  heroFeaturedProductIds: [],
  heroCustomCards: [],
  heroCustomBadgeText: 'CUSTOM UPLOAD',
  heroFloatingCardsEnabled: true,
  heroPreviewMode: 'catalog_products',
  heroAutoSlideEnabled: true,
  heroAutoSlideInterval: 4000,
  heroTrustBadges: [
    { id: 'badge-1', label: 'Instant Download', icon: 'Download', enabled: true },
    { id: 'badge-2', label: '100% Editable Files', icon: 'Layers', enabled: true },
    { id: 'badge-3', label: 'Secure Checkout', icon: 'ShieldCheck', enabled: true },
    { id: 'badge-4', label: 'Commercial License', icon: 'Award', enabled: true },
    { id: 'badge-5', label: 'Premium Quality', icon: 'Sparkles', enabled: true },
    { id: 'badge-6', label: 'Verified Marketplace', icon: 'CheckCircle2', enabled: true },
    { id: 'badge-7', label: '24/7 Support', icon: 'Headphones', enabled: true }
  ],
  heroVisibility: true,
  heroGradient: 'blue-indigo-cyan',
  heroAnimationEnabled: true,
  contactEmail: 'support@rheditography.net',
  contactPhone: '+1 (800) 555-0199',
  contactAddress: '4141 Hacienda Drive, Suite 100, Pleasanton, CA 94588, USA',
  contactHours: '24/7 Digital Delivery',
  contactHeroTitle: 'We are here to help you',
  contactHeroSubtitle: 'Have questions about document templates or custom requests? Get in touch with our support team.',


  nowpaymentsApiKey: '',
  nowpaymentsIpnSecret: '',
};

const COLLECTION_NAME = 'cms_content';
const DOC_ID = 'main_cms';
const CACHE_KEY = 'rh_cached_cms';
const PATH_KEY = `${COLLECTION_NAME}/${DOC_ID}`;

let cachedCMS: CMSContent = { ...DEFAULT_CMS_CONTENT };

try {
  const saved = localStorage.getItem(CACHE_KEY);
  if (saved) {
    cachedCMS = { ...DEFAULT_CMS_CONTENT, ...JSON.parse(saved) };
  }
} catch (e) {}

export function getLocalCachedCMS(): CMSContent {
  return cachedCMS;
}

const cmsSubscribers = new Set<(cms: CMSContent) => void>();
let activeCMSUnsub: Unsubscribe | null = null;

export async function fetchCMSFromFirestore(): Promise<CMSContent> {
  const startTime = performance.now();
  try {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    const snap = await getDoc(docRef);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(PATH_KEY, duration, snap.metadata.fromCache ? 'cache' : 'server', 1);

    if (snap.exists()) {
      const data = { ...DEFAULT_CMS_CONTENT, ...snap.data() } as CMSContent;
      cachedCMS = data;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      } catch (e) {}
      return data;
    }
  } catch (err) {
    console.warn('Error reading CMS from Firestore, using default fallback:', err);
  }
  return cachedCMS;
}

export function subscribeToCMSFromFirestore(callback: (cms: CMSContent) => void): () => void {
  // Deliver cached instantly for 0ms response
  callback(cachedCMS);

  cmsSubscribers.add(callback);

  if (!activeCMSUnsub) {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    activeCMSUnsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = { ...DEFAULT_CMS_CONTENT, ...snap.data() } as CMSContent;
        cachedCMS = data;
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch (e) {}
        cmsSubscribers.forEach((cb) => {
          try { cb(data); } catch (e) {}
        });
      }
    }, (err) => {
      console.warn('Real-time CMS listener notice, falling back to one-time fetch:', err);
      fetchCMSFromFirestore().then((data) => {
        if (data) {
          cachedCMS = data;
          cmsSubscribers.forEach((cb) => {
            try { cb(data); } catch (e) {}
          });
        }
      }).catch(() => {});
    });
  }

  return () => {
    cmsSubscribers.delete(callback);
    if (cmsSubscribers.size === 0 && activeCMSUnsub) {
      activeCMSUnsub();
      activeCMSUnsub = null;
    }
  };
}

export async function saveCMSToFirestore(data: Partial<CMSContent>): Promise<boolean> {
  try {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    const sanitizedData: any = JSON.parse(JSON.stringify(data));

    if (sanitizedData.heroCustomCards && Array.isArray(sanitizedData.heroCustomCards)) {
      sanitizedData.heroCustomCards = await Promise.all(
        sanitizedData.heroCustomCards.map(async (card: any) => {
          if (card.imageUrl && card.imageUrl.startsWith('data:') && card.imageUrl.length > 100000) {
            card.imageUrl = await compressDataUrl(card.imageUrl, 800, 0.7);
          }
          return card;
        })
      );
    }

    if (sanitizedData.heroBgImageUrl && sanitizedData.heroBgImageUrl.startsWith('data:') && sanitizedData.heroBgImageUrl.length > 100000) {
      sanitizedData.heroBgImageUrl = await compressDataUrl(sanitizedData.heroBgImageUrl, 1000, 0.7);
    }

    const merged = { ...cachedCMS, ...sanitizedData };
    cachedCMS = merged;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
    } catch (e) {}

    // Instantly notify all subscribers across the active app
    cmsSubscribers.forEach((cb) => {
      try { cb(merged); } catch (e) {}
    });

    await setDoc(docRef, { ...sanitizedData, updatedAt: new Date().toISOString() }, { merge: true });
    return true;
  } catch (err) {
    console.error('Failed to save CMS settings to Firestore:', err);
    throw err;
  }
}
